import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import FormHelperText from '@mui/material/FormHelperText';
import FormLabel from '@mui/material/FormLabel';
import Grid from '@mui/material/Grid';
import { styled } from '@mui/material/styles';
import { useState, useCallback, ReactNode, useEffect } from 'react';
import { IconButton } from '@/wrappers/IconButton';
import intersection from '../../utils/helpers/array/intersection';
import not from '../../utils/helpers/array/not';
import handleSort from '../../utils/helpers/array/sort';
import { Content } from './Content';

export type IItemType = {
    id: string;
    isDivider?: boolean;
    disabled?: boolean;
    shouldDisplay?: (ids: string[]) => boolean;
    name: ReactNode;
};

export type ITransferListProps = {
    filteredItems?: boolean;
    height?: number;
    error?: boolean;
    helperText?: string;
    items: IItemType[];
    label?: string;
    readOnly?: boolean;
    required?: boolean;
    titleMoveAllLeft?: string;
    titleMoveAllRight?: string;
    titleMoveSelectedLeft?: string;
    titleMoveSelectedRight?: string;
    value?: IItemType['id'][];
    onChange?: (values: string[]) => void;
};

const StyledContainer = styled(Grid, {
    shouldForwardProp: (props) => props !== 'height'
})(
    ({ height }) => `
        display: grid;
        grid-template: ${height}px / 1fr auto 1fr;
        align-items: stretch;
    `
);
const StyledControls = styled(Grid)(
    ({ theme }) => `
        margin-inline: ${theme.spacing(2)};
        align-self: center;
    `
);
const StyledControlsContent = styled(Grid)(
    ({ theme }) => `
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: ${theme.spacing(2)};
    `
);

export const TransferList = ({
    error = false,
    filteredItems = false,
    height = 210,
    helperText,
    items = [],
    label,
    readOnly = false,
    required = false,
    value = [],
    titleMoveAllRight = 'Move All Right',
    titleMoveAllLeft = 'Move All Left',
    titleMoveSelectedLeft = 'Move Selected Left',
    titleMoveSelectedRight = 'Move Selected Right',
    onChange
}: ITransferListProps) => {
    const [checked, setChecked] = useState<string[]>([]);
    const [right, setRight] = useState<string[]>(value);
    const getByIds = (content: IItemType[], ids: string[]): IItemType[] =>
        content.filter(
            ({ id, shouldDisplay }) =>
                ids.includes(id) && (typeof shouldDisplay === 'undefined' || shouldDisplay(right))
        );
    const left = not(items.map(({ id }) => id).sort(handleSort), right);
    const leftItems = getByIds(items, left);
    const rightItems = getByIds(items, right);
    const leftChecked = intersection(
        checked,
        leftItems.map(({ id }) => id)
    );
    const rightChecked = intersection(
        checked,
        rightItems.map(({ id }) => id)
    );

    const handleToggle = useCallback(
        (toggled: string) => {
            if (!readOnly) {
                const currentIndex = checked.indexOf(toggled);

                if (currentIndex === -1) {
                    setChecked([...checked, toggled]);
                } else {
                    setChecked(checked.filter((checkedId) => checkedId !== toggled));
                }
            }
        },
        [checked, readOnly]
    );
    const handleCheckedRight = useCallback(() => {
        if (!readOnly) {
            setRight(right.concat(leftChecked));
            setChecked(not(checked, leftChecked));
        }
    }, [checked, leftChecked, readOnly, right]);
    const handleCheckedLeft = useCallback(() => {
        if (!readOnly) {
            setRight(not(right, rightChecked));
            setChecked(not(checked, rightChecked));
        }
    }, [checked, readOnly, right, rightChecked]);
    const handleAllRight = useCallback(() => {
        if (!readOnly) {
            const toMove = filteredItems ? items.filter((item) => left.includes(item.id)).map((item) => item.id) : left;

            setRight(right.concat(toMove));
        }
    }, [items, left, right, readOnly, filteredItems]);
    const handleAllLeft = useCallback(() => {
        if (!readOnly) {
            const toMove = filteredItems
                ? items.filter((item) => right.includes(item.id)).map((item) => item.id)
                : right;

            setRight(not(right, toMove));
        }
    }, [items, readOnly, right, filteredItems]);

    useEffect(() => {
        if (onChange) {
            onChange(right);
        }
    }, [right, onChange]);

    return (
        <>
            {label && (
                <FormLabel error={error} required={required}>
                    {label}
                </FormLabel>
            )}
            <StyledContainer container height={height} data-testid="transfer-list">
                <Grid>
                    <Content error={error} items={leftItems} checked={checked} onToggle={handleToggle} />
                </Grid>
                <StyledControls>
                    <StyledControlsContent container>
                        <IconButton
                            color="primary"
                            data-testid="moveAllRight"
                            disabled={readOnly || leftItems.length === 0}
                            name="moveAllRight"
                            title={titleMoveAllRight}
                            onClick={handleAllRight}
                        >
                            <KeyboardDoubleArrowRightIcon />
                        </IconButton>
                        <IconButton
                            color="primary"
                            data-testid="moveSelectedRight"
                            disabled={readOnly || leftChecked.length === 0}
                            name="moveSelectedRight"
                            title={titleMoveSelectedRight}
                            onClick={handleCheckedRight}
                        >
                            <KeyboardArrowRightIcon />
                        </IconButton>
                        <IconButton
                            color="primary"
                            data-testid="moveSelectedLeft"
                            disabled={readOnly || rightChecked.length === 0}
                            name="moveSelectedLeft"
                            title={titleMoveSelectedLeft}
                            onClick={handleCheckedLeft}
                        >
                            <KeyboardArrowLeftIcon />
                        </IconButton>
                        <IconButton
                            color="primary"
                            data-testid="moveAllLeft"
                            disabled={readOnly || rightItems.length === 0}
                            name="moveAllLeft"
                            title={titleMoveAllLeft}
                            onClick={handleAllLeft}
                        >
                            <KeyboardDoubleArrowLeftIcon />
                        </IconButton>
                    </StyledControlsContent>
                </StyledControls>
                <Grid>
                    <Content error={error} items={rightItems} checked={checked} onToggle={handleToggle} />
                </Grid>
            </StyledContainer>
            {helperText && <FormHelperText error={error}>{helperText}</FormHelperText>}
        </>
    );
};
